import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
 ArrowLeft,
 Save,
 CheckCircle,
 ChevronDown,
 AlertCircle
} from 'lucide-react'
import { Button, VehicleFormSkeleton } from '../../components/ui'
import { Stepper } from '../../components/vehicles'
import {
 VehicleRegistrationStep1,
 VehicleRegistrationStep2,
 VehicleRegistrationStep3,
 VehicleRegistrationStep4,
 VehicleRegistrationStep5,
 VehicleRegistrationStep6
} from '../../components/vehicles/steps'
import { VehicleDocument } from '../../components/form'
import { useVehicleOptions } from '../../hooks/useVehicleOptions'
import { apiService } from '../../services/api'
import { getStepValidator, validateStep1 } from '../../utils/vehicleValidation'
import { useBranches } from '../../hooks/useBranches'
import { buildFeaturesByCategoryPayload } from './utils/vehicleFeaturesApi'

interface VehicleFormData {
 branchId?: string
 name: string
 brandId: string
 brandName: string
 modelId: string
 modelName: string
 manufactureYear: string
 modelYear: string
 price: string
 minimumPrice: string
 purchasePrice: string
 hidePricing: boolean
 mileage: string
 fuelType: string
 transmission: string
 color: string
 category: string
 type: string
 condition: string
 status: string
 doors: string
 steering: string
 armorLevel?: string
 specialCategory?: string
 characteristics?: string[]
 features: {
 comfort?: Record<string, boolean>
 safety?: Record<string, boolean>
 technology?: Record<string, boolean>
 convenience?: Record<string, boolean>
 performance?: Record<string, boolean>
 lighting?: Record<string, boolean>
 adas?: Record<string, boolean>
 }
 plate?: string
 chassis?: string
 renavam?: string
 isAuction: boolean
 auctionCategory?: string
 auctionSubtype?: string
 auctionDamageLevel?: string
 isInspected: boolean
 inspectionDate?: string
 description: string
 photos: File[]
 documents: VehicleDocument[]
 videos: File[]
 videoUrl?: string
 instagramUrl?: string
 youtubeUrl?: string
 tiktokUrl?: string
 facebookUrl?: string
 motorcycleEngineType?: string
 motorcycleCoolingType?: string
 motorcycleStarterType?: string
 motorcycleBrakeType?: string
 motorcycleFrontBrakeType?: string
 motorcycleRearBrakeType?: string
 motorcycleFeedingType?: string
 motorcycleStyle?: string
 motorcycleCategory?: string
 motorcycleCylinderCapacity?: string
 motorcycleGears?: string
 motorcycleSecondaryColor?: string
 // Caminhões
 truckBodyType?: string
 truckCabType?: string
 truckAxles?: string
 truckTraction?: string
 truckGrossVehicleWeight?: string
 truckPayload?: string
 truckTires?: string
 // Ônibus
 busBodyBrand?: string
 busBodyModel?: string
 busOperationType?: string
 busFleetNumber?: string
 busSeatedCapacity?: string
 busStandingCapacity?: string
 busWheelchairCapacity?: string
}

export const VehicleRegistrationPage: React.FC = () => {
 const navigate = useNavigate()
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [errors, setErrors] = useState<Record<string, string>>({})
 const [successMessage, setSuccessMessage] = useState('')
 const [currentStep, setCurrentStep] = useState(1)
 const totalSteps = 6

 const { 
 options: vehicleOptions,
 isLoading: optionsLoading,
 error: optionsError
 } = useVehicleOptions()

 const { branches } = useBranches()

 const [formData, setFormData] = useState<VehicleFormData>({
 branchId: '',
 name: '',
 brandId: '',
 brandName: '',
 modelId: '',
 modelName: '',
 manufactureYear: '',
 modelYear: '',
 price: '',
 minimumPrice: '',
 purchasePrice: '',
 hidePricing: false,
 mileage: '',
 fuelType: '',
 transmission: '',
 color: '',
 category: '',
 type: '',
 condition: '',
 status: '',
 doors: '',
 steering: '',
 armorLevel: '',
 specialCategory: '',
 characteristics: [],
 features: {},
 plate: '',
 chassis: '',
 renavam: '',
 isAuction: false,
 auctionCategory: '',
 auctionSubtype: '',
 auctionDamageLevel: '',
 isInspected: false,
 inspectionDate: '',
 description: '',
 photos: [],
 documents: [],
 videos: [],
 videoUrl: '',
 instagramUrl: '',
 youtubeUrl: '',
 tiktokUrl: '',
 facebookUrl: '',
 motorcycleEngineType: '',
 motorcycleCoolingType: '',
 motorcycleStarterType: '',
 motorcycleBrakeType: '',
 motorcycleFrontBrakeType: '',
 motorcycleRearBrakeType: '',
 motorcycleFeedingType: '',
 motorcycleStyle: '',
 motorcycleCategory: '',
 motorcycleCylinderCapacity: '',
 motorcycleGears: '',
 motorcycleSecondaryColor: '',
 truckBodyType: '',
 truckCabType: '',
 truckAxles: '',
 truckTraction: '',
 truckGrossVehicleWeight: '',
 truckPayload: '',
 truckTires: '',
 busBodyBrand: '',
 busBodyModel: '',
 busOperationType: '',
 busFleetNumber: '',
 busSeatedCapacity: '',
 busStandingCapacity: '',
 busWheelchairCapacity: '',
 })

 useEffect(() => {
 if (formData.branchId) return
 if (!branches.length) return

 const defaultBranch = branches.find((branch) => branch.isActive && branch.isPrimary)
 ?? branches.find((branch) => branch.isActive)

 if (!defaultBranch) return

 setFormData((prev) => {
 if (prev.branchId) return prev
 return { ...prev, branchId: defaultBranch.id }
 })
 }, [branches, formData.branchId])

 const [primaryPhotoIndex, setPrimaryPhotoIndex] = useState<number>(0)
 
 const errorsSectionRef = useRef<HTMLDivElement>(null)
 const formContainerRef = useRef<HTMLDivElement>(null)

 const handleInputChange = useCallback((field: string, value: any) => {
 setFormData(prev => ({ ...prev, [field]: value }))
 setErrors(prev => {
 if (prev[field]) {
 const newErrors = { ...prev }
 delete newErrors[field]
 return newErrors
 }
 return prev
 })
 }, [])

 // Função para fazer scroll suave ao topo
 const scrollToTop = () => {
 // Usa requestAnimationFrame para garantir que a renderização do novo step aconteceu
 requestAnimationFrame(() => {
 if (formContainerRef.current) {
 formContainerRef.current.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 } else {
 window.scrollTo({ 
 top: 0, 
 behavior: 'smooth' 
 })
 }
 })
 }

 const nextStep = () => {
 // Valida o step atual antes de avançar
 const validation =
 currentStep === 1
 ? validateStep1(formData, branches)
 : getStepValidator(currentStep)(formData)
 
 if (!validation.isValid) {
 console.log('❌ Validação falhou no step', currentStep, '- Erros:', validation.errors)
 setErrors(validation.errors)
 scrollToTop()
 return
 }
 
 // Limpa erros e avança
 setErrors({})
 if (currentStep < totalSteps) {
 setCurrentStep(currentStep + 1)
 scrollToTop()
 }
 }

 const prevStep = () => {
 if (currentStep > 1) {
 setCurrentStep(currentStep - 1)
 scrollToTop()
 }
 }

 const goToStep = (step: number) => {
 if (step >= 1 && step <= totalSteps) {
 setCurrentStep(step)
 scrollToTop()
 }
 }

 const removePhoto = (index: number) => {
 const newPhotos = formData.photos.filter((_, i) => i !== index)
 setFormData(prev => ({ ...prev, photos: newPhotos }))
 
 if (newPhotos.length === 0) {
 setPrimaryPhotoIndex(0)
 } else if (index === primaryPhotoIndex) {
 setPrimaryPhotoIndex(0)
 } else if (index < primaryPhotoIndex) {
 setPrimaryPhotoIndex(primaryPhotoIndex - 1)
 }
 }

 const uploadPhotos = async (): Promise<string[]> => {
 if (formData.photos.length === 0) {
 return []
 }

 const reorderedPhotos = [...formData.photos]
 if (primaryPhotoIndex !== 0) {
 const primaryPhoto = reorderedPhotos[primaryPhotoIndex]
 reorderedPhotos.splice(primaryPhotoIndex, 1)
 reorderedPhotos.unshift(primaryPhoto)
 }

 const formDataPhotos = new FormData()
 reorderedPhotos.forEach((photo) => {
 formDataPhotos.append('photos', photo)
 })

 const response = await apiService.uploadTempVehiclePhotos(formDataPhotos)
 return response.photos.map((photo: { filename: string; url: string }) => photo.filename)
 }

 const uploadDocuments = async (): Promise<Array<{ filename: string; category: string }>> => {
 if (formData.documents.length === 0) {
 return []
 }

 const formDataDocuments = new FormData()
 formData.documents.forEach((doc, index) => {
 formDataDocuments.append('docs', doc.file)
 formDataDocuments.append(`categories[${index}]`, doc.category)
 })

 const response = await apiService.uploadTempVehicleDocuments(formDataDocuments)
 return response.documents.map((doc: { filename: string; url: string; category?: string }, index: number) => ({
 filename: doc.filename,
 category: doc.category || formData.documents[index]?.category || 'other'
 }))
 }

 const validateForm = () => {
 // Validação final apenas para garantir que os campos críticos estão preenchidos
 // As validações detalhadas já foram feitas em cada step
 const newErrors: Record<string, string> = {}

 if (!formData.name.trim()) {
 newErrors.name = 'Nome é obrigatório'
 }

 if (!formData.brandId) {
 newErrors.brand = 'Marca é obrigatória'
 }

 if (!formData.modelId) {
 newErrors.model = 'Modelo é obrigatório'
 }

 if (!formData.description.trim()) {
 newErrors.description = 'Descrição é obrigatória'
 } else if (formData.description.length < 10) {
 newErrors.description = 'Descrição deve ter pelo menos 10 caracteres'
 }

 setErrors(newErrors)
 
 if (Object.keys(newErrors).length > 0) {
 console.log('❌ Validação final falhou:', newErrors)
 setTimeout(() => {
 errorsSectionRef.current?.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 }, 100)
 }
 
 return Object.keys(newErrors).length === 0
 }

 const handleSubmit = async (e?: React.MouseEvent) => {
 if (e) {
 e.preventDefault()
 e.stopPropagation()
 }
 
 console.log('🔍 handleSubmit chamado - Step atual:', currentStep, '/', totalSteps)
 
 // PROTEÇÃO CRÍTICA: Só permite submit no último step (Step 6)
 if (currentStep !== totalSteps) {
 console.log('🚫🚫🚫 SUBMIT BLOQUEADO - NÃO ESTÁ NO ÚLTIMO STEP 🚫🚫🚫')
 console.log('🚫 Step atual:', currentStep, '- Precisa estar no step:', totalSteps)
 return
 }
 
 console.log('✅ SUBMIT PERMITIDO - Está no último step (resumo)')
 
 if (isSubmitting) {
 console.log('⚠️ Já está submetendo...')
 return
 }
 
 if (!validateForm()) {
 console.log('❌ Validação do formulário falhou')
 return
 }

 console.log('🚀 Iniciando envio do formulário...')
 setIsSubmitting(true)
 try {
 // Upload de documentos PRIMEIRO
 let tempDocuments: Array<{ filename: string; category: string }> = []
 if (formData.documents.length > 0) {
 console.log('📄 Fazendo upload de', formData.documents.length, 'documentos...')
 tempDocuments = await uploadDocuments()
 console.log('✅ Upload de documentos concluído:', tempDocuments)
 }

 // Upload de fotos DEPOIS
 let tempPhotos: string[] = []
 if (formData.photos.length > 0) {
 console.log('📸 Fazendo upload de', formData.photos.length, 'fotos...')
 tempPhotos = await uploadPhotos()
 console.log('✅ Upload de fotos concluído:', tempPhotos)
 }

 // Limpa o preço para enviar à API
 const priceClean = formData.price.replace(/[^\d,]/g, '').replace(',', '.')
 const priceNumber = parseFloat(priceClean)
 
 console.log('💰 Preço formatado:', {
 original: formData.price,
 clean: priceClean,
 number: priceNumber
 })
 
 if (isNaN(priceNumber) || priceNumber <= 0) {
 console.error('❌ Preço inválido:', { priceClean, priceNumber })
 throw new Error('Preço inválido. Verifique o valor e tente novamente.')
 }

 const plateEnd = formData.plate && formData.plate.length > 0 
 ? formData.plate.slice(-1) 
 : undefined

 const vehicleData: any = {
 basic: {
 branchId: formData.branchId || undefined,
 name: formData.name,
 category: formData.category,
 condition: formData.condition,
 status: formData.status,
 type: formData.type || undefined,
 description: formData.description || undefined
 },
 catalog: {
 brandId: formData.brandId,
 modelId: formData.modelId,
 modelYear: parseInt(formData.modelYear),
 manufactureYear: parseInt(formData.manufactureYear)
 },
 identification: {
 plate: formData.plate || undefined,
 plateEnd: plateEnd,
 chassis: formData.chassis || undefined,
 renavam: formData.renavam || undefined
 },
 specifications: {
 engine: {
 type: formData.fuelType || undefined,
 transmission: formData.transmission || undefined,
 engine: undefined,
 powerHp: undefined,
 displacement: undefined,
 cylinders: undefined,
 turbo: undefined
 },
 consumption: {
 city: undefined,
 highway: undefined,
 tankCapacity: undefined
 },
 body: {
 doors: formData.doors ? parseInt(formData.doors) : undefined,
 seats: undefined,
 color: formData.color || undefined,
 colorName: undefined,
 secondaryColor: formData.motorcycleSecondaryColor || undefined,
 secondaryColorName: undefined
 },
 mileage: formData.mileage ? parseInt(formData.mileage.replace(/\./g, '')) : 0
 },
 commercial: {
 inspected: formData.isInspected || false,
 inspectionDate: formData.isInspected && formData.inspectionDate 
 ? new Date(formData.inspectionDate).toISOString() 
 : undefined,
 videoUrl: formData.videoUrl || undefined,
 acceptsTrade: false,
 armorLevel: formData.armorLevel || undefined,
 specialCategory: formData.specialCategory || undefined,
 steeringType: formData.steering || undefined,
 warranty: {
 months: undefined,
 endDate: undefined
 }
 },
 auction: {
 isAuction: formData.isAuction || false,
 category: formData.auctionCategory || undefined,
 subtype: formData.auctionSubtype || undefined,
 date: undefined,
 house: undefined,
 lot: undefined,
 damageLevel: formData.auctionDamageLevel || undefined,
 notes: undefined
 },
 pricing: {
 sale: {
 price: priceNumber,
 minimumPrice: formData.minimumPrice 
 ? parseInt(formData.minimumPrice.replace(/\D/g, '')) / 100
 : undefined,
 hidePrice: formData.hidePricing || false
 },
 purchase: formData.purchasePrice
 ? {
 price: parseInt(formData.purchasePrice.replace(/\D/g, '')) / 100,
 date: undefined
 }
 : undefined
 },
 featuresByCategory: buildFeaturesByCategoryPayload(formData.category, formData.features),
 characteristics: formData.characteristics || []
 }

 if (formData.category === 'A') {
 vehicleData.specifications.motorcycle = {
 cylinderCapacity: formData.motorcycleCylinderCapacity
 ? parseInt(formData.motorcycleCylinderCapacity)
 : undefined,
 coolingType: formData.motorcycleCoolingType || undefined,
 starterType: formData.motorcycleStarterType || undefined,
 engineType: formData.motorcycleEngineType || undefined,
 gears: formData.motorcycleGears ? parseInt(formData.motorcycleGears) : undefined,
 frontBrake: formData.motorcycleFrontBrakeType || undefined,
 rearBrake: formData.motorcycleRearBrakeType || undefined,
 feedingType: formData.motorcycleFeedingType || undefined,
 style: formData.motorcycleStyle || undefined,
 category: formData.motorcycleCategory || undefined
 }
 }

 if (formData.category === 'C') {
 vehicleData.specifications.truck = {
 bodyType: formData.truckBodyType || undefined,
 cabType: formData.truckCabType || undefined,
 traction: formData.truckTraction || undefined,
 axles: formData.truckAxles ? parseInt(formData.truckAxles) : undefined,
 grossVehicleWeight: formData.truckGrossVehicleWeight
 ? parseInt(formData.truckGrossVehicleWeight)
 : undefined,
 payload: formData.truckPayload ? parseInt(formData.truckPayload) : undefined,
 tires: formData.truckTires ? parseInt(formData.truckTires) : undefined
 }
 }

 if (formData.category === 'D') {
 vehicleData.specifications.bus = {
 bodyBrand: formData.busBodyBrand || undefined,
 bodyModel: formData.busBodyModel || undefined,
 operationType: formData.busOperationType || undefined,
 fleetNumber: formData.busFleetNumber || undefined,
 seatedCapacity: formData.busSeatedCapacity ? parseInt(formData.busSeatedCapacity) : undefined,
 standingCapacity: formData.busStandingCapacity ? parseInt(formData.busStandingCapacity) : undefined,
 wheelchairCapacity: formData.busWheelchairCapacity ? parseInt(formData.busWheelchairCapacity) : undefined,
 }
 }

 if (tempPhotos.length > 0) {
 vehicleData.tempPhotos = tempPhotos
 }

 if (tempDocuments.length > 0) {
 vehicleData.tempDocuments = tempDocuments
 }

 if (formData.instagramUrl) vehicleData.instagramUrl = formData.instagramUrl
 if (formData.youtubeUrl) vehicleData.youtubeUrl = formData.youtubeUrl
 if (formData.tiktokUrl) vehicleData.tiktokUrl = formData.tiktokUrl
 if (formData.facebookUrl) vehicleData.facebookUrl = formData.facebookUrl

 console.log('🎯 Dados de leilão do formData:', {
 isAuction: formData.isAuction,
 auctionCategory: formData.auctionCategory,
 auctionSubtype: formData.auctionSubtype,
 auctionDamageLevel: formData.auctionDamageLevel
 })
 console.log('🎯 Dados de leilão enviados:', vehicleData.auction)
 console.log('🚗 Dados do veículo preparados (estrutura completa):', JSON.stringify(vehicleData, null, 2))

 const response = await apiService.createVehicle(vehicleData)
 console.log('✅ Veículo criado:', response)
 setSuccessMessage('Veículo cadastrado com sucesso!')
 
 setTimeout(() => {
 navigate('/vehicles')
 }, 2000)
 
 } catch (error) {
 console.error('❌ Erro ao cadastrar veículo:', error)
 setErrors({ 
 submit: error instanceof Error ? error.message : 'Erro ao cadastrar veículo. Tente novamente.' 
 })
 
 setTimeout(() => {
 errorsSectionRef.current?.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 }, 100)
 } finally {
 setIsSubmitting(false)
 }
 }

 if (optionsLoading) {
 return <VehicleFormSkeleton />
 }

 return (
 <div className="max-w-5xl mx-auto space-y-6" ref={formContainerRef}>
 <div className="flex items-center space-x-4">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate('/vehicles')}
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 Voltar
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-foreground">
 Novo Veículo
 </h1>
 <p className="text-muted-foreground">
 Cadastre um novo veículo em {totalSteps} etapas
 </p>
 </div>
 </div>

 <Stepper
 currentStep={currentStep}
 totalSteps={totalSteps}
 steps={[
 'Informações Básicas',
 'Preços e Status',
 'Especificações',
 'Documentação',
 'Fotos e Mídias',
 'Resumo Final'
 ]}
 onStepClick={goToStep}
 vehicleName={formData.name || undefined}
 />

 {successMessage && (
 <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
 <div className="flex items-center">
 <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
 <p className="text-green-800 dark:text-green-200">{successMessage}</p>
 </div>
 </div>
 )}

 {optionsError && (
 <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
 <p className="text-red-800 dark:text-red-200">{optionsError}</p>
 </div>
 </div>
 )}

 {errors.submit && (
 <div 
 ref={errorsSectionRef}
 className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
 >
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
 <p className="text-red-800 dark:text-red-200">{errors.submit}</p>
 </div>
 </div>
 )}
 
 {Object.keys(errors).length > 0 && !errors.submit && (
 <div 
 ref={errorsSectionRef}
 className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
 >
 <div className="flex items-start">
 <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
 <div className="flex-1">
 <p className="font-medium text-red-800 dark:text-red-200 mb-2">
 Corrija os seguintes erros:
 </p>
 <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
 {Object.entries(errors).map(([field, message]) => (
 <li key={field}>{message}</li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 )}

 <div 
 onKeyDown={(e) => {
 // Bloqueia Enter em todo o formulário
 if (e.key === 'Enter') {
 e.preventDefault()
 e.stopPropagation()
 console.log('🚫 Enter bloqueado - Use os botões')
 }
 }}
 className="space-y-6"
 >
 {currentStep === 1 && (
 <VehicleRegistrationStep1
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {currentStep === 2 && (
 <VehicleRegistrationStep2
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}
 />
 )}

 {currentStep === 3 && (
 <VehicleRegistrationStep3
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {currentStep === 4 && (
 <VehicleRegistrationStep4
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {currentStep === 5 && (
 <VehicleRegistrationStep5
 formData={formData}
 errors={errors}
 primaryPhotoIndex={primaryPhotoIndex}
 onInputChange={handleInputChange}
 onPrimaryPhotoChange={setPrimaryPhotoIndex}
 />
 )}

 {currentStep === 6 && (
 <VehicleRegistrationStep6
 formData={formData}
 vehicleOptions={vehicleOptions}
 />
 )}

 <div className="flex items-center justify-between pt-6 border-t border-border">
 <Button
 type="button"
 variant="outline"
 onClick={prevStep}
 disabled={currentStep === 1}
 className="flex items-center"
 >
 <ChevronDown className="h-4 w-4 mr-2 rotate-90" />
 Anterior
 </Button>

 <div className="text-sm text-muted-foreground">
 Etapa {currentStep} de {totalSteps}
 </div>

 {currentStep < totalSteps ? (
 <Button
 type="button"
 onClick={nextStep}
 className="flex items-center"
 >
 Próxima
 <ChevronDown className="h-4 w-4 ml-2 -rotate-90" />
 </Button>
 ) : (
 <Button
 type="button"
 onClick={(e) => {
 console.log('🖱️ Botão "Salvar Veículo" clicado')
 handleSubmit(e as any)
 }}
 className="flex items-center"
 disabled={!formData.category || isSubmitting}
 >
 {isSubmitting ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
 Salvando...
 </>
 ) : (
 <>
 <Save className="h-4 w-4 mr-2" />
 Salvar Veículo
 </>
 )}
 </Button>
 )}
 </div>
 </div>
 </div>
 )
}

